import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useSnackbar } from 'notistack';
import zoneService from '../../services/zoneService';
import { Zone, ZoneStats } from '../../types/zone';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Alert,
  Tooltip,
} from '@mui/material';
import { Edit, Delete, Add, LocationOn, AttachMoney } from '@mui/icons-material';
import CreateZoneDialog from './CreateZoneDialog';
import EditZoneDialog from './EditZoneDialog';

// Format price with thousands separators and currency
const formatPrice = (price: number | string): string => {
  if (price === null || price === undefined) return 'N/A';
  const num = typeof price === 'string' ? parseFloat(price) : price;
  if (isNaN(num)) return 'N/A';
  return new Intl.NumberFormat('fr-FR', {
    style: 'decimal',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(num) + ' F CFA';
};

// Format date string to localized date
const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return 'N/A';
  try {
    // Handle both ISO strings and other date formats
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      console.warn('Invalid date string:', dateString);
      return 'Invalid Date';
    }
    return new Intl.DateTimeFormat('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'UTC' // Ensure consistent timezone handling
    }).format(date);
  } catch (error) {
    console.error('Error formatting date:', dateString, error);
    return 'Invalid Date';
  }
};

const ZonesList: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();

  
  const [zones, setZones] = useState<Zone[]>([]);
  const [stats, setStats] = useState<ZoneStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedZone, setSelectedZone] = useState<Zone | null>(null);
  const [deletingZoneId, setDeletingZoneId] = useState<number | null>(null);

  // Check if user is admin
  const isAdmin = user?.role === 'admin';

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [zonesData, statsData] = await Promise.all([
        zoneService.getZones(),
        zoneService.getZoneStats(),
      ]);
      
      setZones(zonesData);
      setStats(statsData);
    } catch (error) {
      console.error('[ZonesList] Error fetching data:', error);
      setError('Failed to load zones data');
      enqueueSnackbar(t('zones.messages.error'), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreateZone = async (zoneData: { name: string; region: string; price_per_kg: number }) => {
    try {
      const newZone = await zoneService.createZone(zoneData);
      setZones(prev => [...prev, newZone]);
      setCreateDialogOpen(false);
      enqueueSnackbar(t('zones.messages.created'), { variant: 'success' });
      
      // Refresh stats
      const newStats = await zoneService.getZoneStats();
      setStats(newStats);
    } catch (error: any) {
      console.error('Error creating zone:', error);
      const errorMessage = error.response?.data?.message || error.message || t('zones.messages.createError');
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  };

  const handleEditZone = async (zoneData: { name: string; region: string; price_per_kg: number }) => {
    if (!selectedZone) return;
    
    try {
      const updatedZone = await zoneService.updateZone(selectedZone.id, zoneData);
      setZones(prev => prev.map(zone => zone.id === selectedZone.id ? updatedZone : zone));
      setEditDialogOpen(false);
      setSelectedZone(null);
      enqueueSnackbar(t('zones.messages.updated'), { variant: 'success' });
      
      // Refresh stats
      const newStats = await zoneService.getZoneStats();
      setStats(newStats);
    } catch (error: any) {
      console.error('Error updating zone:', error);
      const errorMessage = error.response?.data?.message || error.message || t('zones.messages.updateError');
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  };

  const handleDeleteClick = (zone: Zone) => {
    setSelectedZone(zone);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedZone) return;
    
    try {
      setDeletingZoneId(selectedZone.id);
      await zoneService.deleteZone(selectedZone.id);
      setZones(prev => prev.filter(zone => zone.id !== selectedZone.id));
      setDeleteDialogOpen(false);
      setSelectedZone(null);
      enqueueSnackbar(t('zones.messages.deleted'), { variant: 'success' });
      
      // Refresh stats
      const newStats = await zoneService.getZoneStats();
      setStats(newStats);
    } catch (error: any) {
      console.error('Error deleting zone:', error);
      const errorMessage = error.response?.data?.message || error.message || t('zones.messages.deleteError');
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setDeletingZoneId(null);
    }
  };

  const handleEditClick = (zone: Zone) => {
    setSelectedZone(zone);
    setEditDialogOpen(true);
  };

  const getRegionColor = (region: string) => {
    if (region.toLowerCase().includes('national')) {
      return 'primary';
    } else if (region.toLowerCase().includes('west africa')) {
      return 'secondary';
    } else {
      return 'default';
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('fr-BF', {
      style: 'currency',
      currency: 'XOF',
      minimumFractionDigits: 0,
    }).format(price);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-BF', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">{t('zones.title')}</Typography>
        {isAdmin && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            onClick={() => setCreateDialogOpen(true)}
          >
            {t('zones.createNew')}
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
          <Button onClick={fetchData} variant="outlined" size="small" sx={{ ml: 2 }}>
            {t('common.retry')}
          </Button>
        </Alert>
      )}

      {stats && (
        <Box display="flex" gap={2} mb={3} flexWrap="wrap">
          <StatCard
            title={t('zones.stats.total')}
            value={stats.total}
            color="primary"
            icon={<LocationOn />}
          />
          <StatCard
            title={t('zones.stats.national')}
            value={stats.national}
            color="success"
            icon={<LocationOn />}
          />
          <StatCard
            title={t('zones.stats.international')}
            value={stats.international}
            color="info"
            icon={<LocationOn />}
          />
          <StatCard
            title={t('zones.stats.averagePrice')}
            value={formatPrice(stats.averagePrice)}
            color="warning"
            icon={<AttachMoney />}
            isPrice={true}
          />
        </Box>
      )}

      <Card>
        <CardContent>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('zones.fields.id')}</TableCell>
                  <TableCell>{t('zones.fields.name')}</TableCell>
                  <TableCell>{t('zones.fields.region')}</TableCell>
                  <TableCell>{t('zones.fields.pricePerKg')}</TableCell>
                  <TableCell>{t('zones.fields.createdAt')}</TableCell>
                  <TableCell>{t('zones.fields.updatedAt')}</TableCell>
                  {isAdmin && <TableCell>{t('common.actions')}</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {zones.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={isAdmin ? 7 : 6} align="center">
                      <Typography variant="body2" color="textSecondary">
                        {t('zones.messages.noZones')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  zones.map((zone) => (
                    <TableRow key={zone.id} hover>
                      <TableCell>{zone.id}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {zone.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={zone.region}
                          color={getRegionColor(zone.region)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium" color="primary">
                          {formatPrice(zone.price_per_kg)}
                        </Typography>
                      </TableCell>
                      <TableCell>{formatDate(zone.created_at)}</TableCell>
                      <TableCell>{formatDate(zone.updated_at)}</TableCell>
                      {isAdmin && (
                        <TableCell>
                          <Tooltip title={t('common.edit')}>
                            <IconButton 
                              onClick={() => handleEditClick(zone)}
                              size="small"
                              disabled={deletingZoneId === zone.id}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t('common.delete')}>
                            <IconButton 
                              onClick={() => handleDeleteClick(zone)}
                              size="small"
                              color="error"
                              disabled={deletingZoneId === zone.id}
                            >
                              {deletingZoneId === zone.id ? (
                                <CircularProgress size={20} />
                              ) : (
                                <Delete fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Create Zone Dialog */}
      <CreateZoneDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSubmit={handleCreateZone}
      />

      {/* Edit Zone Dialog */}
      <EditZoneDialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setSelectedZone(null);
        }}
        onSubmit={handleEditZone}
        zone={selectedZone}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>{t('zones.deleteDialog.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('zones.deleteDialog.content', { name: selectedZone?.name })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="primary">
            {t('common.cancel')}
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const StatCard = ({ 
  title, 
  value, 
  color, 
  icon, 
  isPrice = false 
}: { 
  title: string; 
  value: number | string; 
  color: string; 
  icon: React.ReactNode;
  isPrice?: boolean;
}) => (
  <Card sx={{ minWidth: 200, flex: 1 }}>
    <CardContent>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box>
          <Typography color="textSecondary" gutterBottom variant="body2">
            {title}
          </Typography>
          <Typography variant="h6" component="div" color={color}>
            {isPrice ? value : typeof value === 'number' ? value.toLocaleString() : value}
          </Typography>
        </Box>
        <Box color={color}>{icon}</Box>
      </Box>
    </CardContent>
  </Card>
);

export default ZonesList;
